import { getCorrelationId } from 'orch-communicator/store/selectors';
import { CheckoutOrchestrator, GAMPEvent, UserTypeShort, RememberMeType } from 'types/gamp';
import { ComplianceSettings } from 'types/vsb-sdk/checkout';
import { InitData } from 'types/vsb-sdk/init';

let initData: InitData;
let srcDpaId: string | undefined;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
let srcSystemName: string;
let gaClientId: string;

const srcDpaIdMap: {
  [key: string]: string;
} = {
  // VCO-14529: Mastercard Payment Gateway Services Limited apikey
  '6CB7SWU7FEU2MNQM6AIM21-4t0oWwhGCh1f8hfeN6DYg3FPLc': 'MPGS',
  // MA apikey
  JK0TVYHU43R1D9M2IU9G21BHE7Wxi584CeZQnZCffv4_o0ezc: 'MASTERCARD'
};

/* START GENAI@GHCOPILOT */
// _ga client ID is formated as 123456789.1234567890
const generateGAMPClientId = (): string => {
  const random = Math.floor(Math.random() * 10000000000);
  return `${Date.now()}.${random}`;
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
// return singleton client ID for GA Measurement Protocol
const getGAMPClientId = (): string => {
  if (!gaClientId) {
    gaClientId = generateGAMPClientId();
  }

  return gaClientId;
};

/* END GENAI@GHCOPILOT */
const recognizedByNetwork: { [key: string]: UserTypeShort } = {};
export const setUserTypeId = (isRecognizedResponses: { [key: string]: boolean }): void => {
  for (const brand in isRecognizedResponses) {
    recognizedByNetwork[`user_type_identification_${brand}`] = isRecognizedResponses[brand]
      ? 'Recognized'
      : 'Unrecognized';
  }
};

/* START: GENAI:GHCOPILOT */
/*
 * GA funnels expect country only - normalize to return 2-letter country code
 * dpaLocale is defined in EMVCo spec as ISO language country pair. [ISO 639-1 Code] [ISO 3166-1 alpha-2 country code]
 * Examples: en-US, en_US, fil-PH
 * Not supported: ar-INT, gr-INT (INT is not a valid country code; should be UNDEFINED in this case)
 */
const getCountry = (dpaLocale: string | undefined): string => {
  if (dpaLocale) {
    if (/[a-z]{2,3}[-_]{1}[a-z]{2}/i.test(dpaLocale)) {
      // en-us or en-US, en_US, fil-PH
      return dpaLocale.substring(dpaLocale.length - 2, dpaLocale.length).toUpperCase();
    } else if (/[a-z]{2}/i.test(dpaLocale)) {
      // us or US
      return dpaLocale.toUpperCase();
    }
  }

  return 'UNDEFINED';
};
/* END: GENAI:GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const getRememberMeType = (
  complianceSettings: ComplianceSettings | undefined
): RememberMeType => {
  if (
    complianceSettings &&
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    complianceSettings.complianceResources &&
    complianceSettings.complianceResources.length > 0
  ) {
    const isRememberMeSelected = complianceSettings.complianceResources.some(resource => {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return resource && resource.complianceType && resource.complianceType === 'REMEMBER_ME';
    });

    if (isRememberMeSelected) {
      return 'SELECTED' as RememberMeType;
    }
  }

  return 'UNDEFINED' as RememberMeType;
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
// MP event names MUST be snake_case
const getGAMPName = (event: string): string => {
  return event.replace(/ /g, '_').toLowerCase();
};
/* END GENAI@GHCOPILOT */

// START GENAI@GHCOPILOT
export const setInitData = (data: InitData): void => {
  initData = data;
};
// END GENAI@GHCOPILOT

export const pushGampData = (data: GAMPEvent) => {
  try {
    // Reduce network noise in development
    if (process.env.NODE_ENV === 'development') {
      return;
    }

    /* START GENAI@GHCOPILOT */
    const measurementId = window.VSB_GLOBALS.gampId || 'G-H3DCNE46F4'; // dev-default
    const apiSecret = window.VSB_GLOBALS.gampApiSecret || 'UmqHjB1OT1-KbjUMJg1UQQ'; // dev-default
    // GA Measurement Protocol direct POST
    const payload = {
      client_id: getGAMPClientId(),
      timestamp_micros: Date.now() * 1000,
      events: [
        {
          name: getGAMPName(data.event || data.event_action || data.event_label || 'undefined'),
          params: {
            ...data
          }
        }
      ]
    };
    /* END GENAI@GHCOPILOT */

    /* START GENAI@GHCOPILOT */
    fetch(
      `https://www.google-analytics.com/mp/collect?measurement_id=${measurementId}&api_secret=${apiSecret}`,
      {
        method: 'POST',
        body: JSON.stringify(payload)
      }
    );

    /* END GENAI@GHCOPILOT */
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(`GA MP push failure for: ${JSON.stringify(e, null, 2)}`);
  }
};

export const getBaseApiEvent = (): GAMPEvent => {
  const apiKey = initData.srcDpaId || initData.customData?.apikey;
  srcDpaId = initData.srcDpaId || apiKey || 'unknown_initiator_id';
  srcSystemName = apiKey && srcDpaIdMap[apiKey] ? srcDpaIdMap[apiKey] : 'VISA';

  const baseApiEvent = {
    api_key: apiKey,
    Checkout_orchestrator: 'merchant' as CheckoutOrchestrator,
    correlation_id: getCorrelationId(),
    dpa_id: initData.dpaData?.srcDpaId || initData.srcDpaId, // or clause is for SRC SDK TESTER INIT DATA
    dpa_name: initData.dpaData?.dpaPresentationName,
    error: undefined,
    event: undefined,
    event_action: undefined,
    event_label: undefined,
    flow: 'VSB',
    merchant_country_code: getCountry(initData.dpaTransactionOptions?.dpaLocale),
    src_initiator_id: srcDpaId
  };

  return Object.assign(baseApiEvent, recognizedByNetwork);
};

/* START GENAI@GHCOPILOT */
export const pushUctpInit = () => {
  const x = 'UCTP Init called';
  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: x
  });
};
/* END GENAI@GHCOPILOT */

// START GENAI@GHCOPILOT
export const pushINOData = (data: InitData | null, eventName: string, measurePrefix: string) => {
  const x = eventName;
  initData = data || initData;
  const measures = performance.getEntriesByType('measure');
  const ino: { [key: string]: number } = {};
  measures.forEach(measure => {
    if (measure.name.indexOf(measurePrefix) === 0) {
      ino[measure.name] = Math.round(measure.duration);
    }
  });

  pushGampData({
    ...getBaseApiEvent(),
    event: x,
    event_action: x,
    event_label: x,
    ...ino
  });
};
// END GENAI@GHCOPILOT

/* START GENAI@GHCOPILOT */
export const pushUctpGetCards = (type: string, error: string | undefined) => {
  const x = `UCTP Get Cards called - ${type}`;
  pushGampData({
    ...getBaseApiEvent(),
    /* START GENAI@GHCOPILOT */
    error: error || 'Undefined',
    /* END GENAI@GHCOPILOT */
    event: x,
    event_action: x,
    event_label: x
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushUctpInitiateIdentityValidation = (
  otpProvider: string,
  error: string | undefined
) => {
  const x = `UCTP Initiate Identity Validation called - OTP Provider: ${otpProvider}`;
  pushGampData({
    ...getBaseApiEvent(),
    /* START GENAI@GHCOPILOT */
    error: error || 'Undefined',
    /* END GENAI@GHCOPILOT */
    event: x,
    event_action: x,
    event_label: x
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushUctpCheckout = (error: string | undefined) => {
  const x = `UCTP Checkout called`;
  pushGampData({
    ...getBaseApiEvent(),
    /* START GENAI@GHCOPILOT */
    error: error || 'Undefined',
    /* END GENAI@GHCOPILOT */
    event: x,
    event_action: x,
    event_label: x
  });
};
/* END GENAI@GHCOPILOT */

/* START GENAI@GHCOPILOT */
export const pushUctpUnbind = (error: string | undefined) => {
  const x = `UCTP Unbind called`;
  pushGampData({
    ...getBaseApiEvent(),
    /* START GENAI@GHCOPILOT */
    error: error || 'Undefined',
    /* END GENAI@GHCOPILOT */
    event: x,
    event_action: x,
    event_label: x
  });
};
/* END GENAI@GHCOPILOT */
